import { useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { X } from "lucide-react";
import Orb from "./Orb";
import AttendeeStrip from "./AttendeeStrip";
import CountdownTimer from "./CountdownTimer";

const fadeUp = {
    initial: { opacity: 0, y: 28 },
    animate: { opacity: 1, y: 0 },
};

/**
 * EPIC hero with centered orb + logo, countdown timer on the right, and modal form.
 */
export default function Hero({ onSuccess, attendeeRefreshKey, showModal, openModal, closeModal }) {

    // Zoom & pan state for info image
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const isDragging = useRef(false);
    const lastPos = useRef({ x: 0, y: 0 });
    const lastPinchDist = useRef(null);

    const resetZoom = useCallback(() => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
        lastPinchDist.current = null;
    }, []);

    const clampZoom = (z) => Math.min(Math.max(z, 1), 5);

    const handleWheel = useCallback((e) => {
        e.preventDefault();
        setZoom((z) => {
            const next = clampZoom(z + (e.deltaY < 0 ? 0.3 : -0.3));
            if (next === 1) setPan({ x: 0, y: 0 });
            return next;
        });
    }, []);

    const handlePointerDown = useCallback((e) => {
        if (e.pointerType === "touch") return; // touches handled via onTouchStart
        isDragging.current = true;
        lastPos.current = { x: e.clientX, y: e.clientY };
        e.currentTarget.setPointerCapture(e.pointerId);
    }, []);

    const handlePointerMove = useCallback((e) => {
        if (!isDragging.current) return;
        setPan((p) => ({ x: p.x + e.clientX - lastPos.current.x, y: p.y + e.clientY - lastPos.current.y }));
        lastPos.current = { x: e.clientX, y: e.clientY };
    }, []);

    const handlePointerUp = useCallback(() => { isDragging.current = false; }, []);

    const getPinchDist = (touches) => {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    };

    const handleTouchStart = useCallback((e) => {
        if (e.touches.length === 2) {
            lastPinchDist.current = getPinchDist(e.touches);
        } else if (e.touches.length === 1 && zoom > 1) {
            lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        }
    }, [zoom]);

    const handleTouchMove = useCallback((e) => {
        if (e.touches.length === 2) {
            e.preventDefault();
            const dist = getPinchDist(e.touches);
            if (lastPinchDist.current) {
                const delta = (dist - lastPinchDist.current) * 0.01;
                setZoom((z) => {
                    const next = clampZoom(z + delta);
                    if (next === 1) setPan({ x: 0, y: 0 });
                    return next;
                });
            }
            lastPinchDist.current = dist;
        } else if (e.touches.length === 1 && zoom > 1) {
            e.preventDefault();
            const touch = e.touches[0];
            setPan((p) => ({ x: p.x + touch.clientX - lastPos.current.x, y: p.y + touch.clientY - lastPos.current.y }));
            lastPos.current = { x: touch.clientX, y: touch.clientY };
        }
    }, [zoom]);

    const handleTouchEnd = useCallback(() => { lastPinchDist.current = null; }, []);

    const handleModalClose = useCallback(() => { resetZoom(); closeModal(); }, [resetZoom, closeModal]);

    return (
        <section
            data-testid="hero-section"
            className="relative overflow-hidden min-h-[100svh] flex flex-col pt-24 pb-24 px-2 sm:px-4"
        >
            {/* Layer 0: Cloudinary background video */}
            <video
                className="absolute inset-0 z-0 w-full h-full object-cover sm:object-contain bg-black/5"
                src="https://res.cloudinary.com/dyssxsxce/video/upload/q_auto/f_auto/v1777460960/AIS_FEST_2026_q9odrf.mp4"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                aria-hidden="true"
                data-testid="hero-video-bg"
            />

            {/* Bright overlay */}
            <div className="absolute inset-0 z-0 bg-white/30 backdrop-blur-[1px]" />

            {/* Soft color blobs for party vibes */}
            <div className="absolute -top-24 -left-24 z-0 w-[520px] h-[520px] rounded-full bg-[#FF5E3A]/10 blur-3xl" />
            <div className="absolute top-1/3 -right-32 z-0 w-[560px] h-[560px] rounded-full bg-[#FACC15]/15 blur-3xl" />
            <div className="absolute bottom-0 left-1/3 z-0 w-[480px] h-[480px] rounded-full bg-[#567f9b]/10 blur-3xl" />

            {/* Orb + AIS logo (centered, moved up 20% on mobile) */}
            <div className="pointer-events-none absolute inset-0 z-[15] flex items-center justify-center pt-0 -translate-y-[20%] lg:translate-y-0">
                <div className="relative w-[min(88vw,740px)] h-[min(88vw,740px)]">
                    <div className="absolute inset-0 pointer-events-auto">
                        <Orb
                            hue={190}
                            hoverIntensity={0.55}
                            rotateOnHover={true}
                        />
                    </div>
                    {/* AIS Logo glowing in the middle of the orb */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="ais-halo relative flex flex-col items-center justify-center -translate-y-4 sm:-translate-y-12">
                            <div className="absolute inset-0 -m-6 rounded-full bg-white/70 backdrop-blur-xl shadow-[0_20px_80px_-10px_rgba(86,127,155,0.4)]" />
                            <img
                                src="https://res.cloudinary.com/dyssxsxce/image/upload/v1777465385/asi-fest-logo-2026_rag6qy.png"
                                alt="AIS FEST 2026"
                                data-testid="hero-ais-logo"
                                className="relative z-10 w-[16.1rem] sm:w-[20.1rem] md:w-[25rem] lg:w-[31.8rem] h-auto select-none mix-blend-multiply"
                                draggable="false"
                            />
                            {/* Hover overlay with REGISTER text */}
                            <div
                                onClick={openModal}
                                className="pointer-events-auto absolute inset-0 -m-6 z-30 rounded-full flex items-center justify-center cursor-pointer group"
                            >
                                <div className="absolute inset-0 rounded-full bg-[#02254F]/0 group-hover:bg-[#02254F]/70 transition-all duration-500" />
                                <span className="relative z-10 text-white text-center font-poppins font-black uppercase text-xl sm:text-2xl md:text-3xl tracking-[0.1em] opacity-0 group-hover:opacity-100 transition-all duration-500 drop-shadow-[0_4px_12px_rgba(0,0,0,0.9)] scale-90 group-hover:scale-100 leading-tight px-2">
                                    Important<br/>Information
                                </span>
                            </div>
                            {/* Join Us text at the bottom of the circle */}
                            <div className="absolute bottom-10 sm:bottom-16 z-40 pointer-events-none">
                                <span className="text-green-400 font-poppins font-black uppercase tracking-[0.3em] text-[1.1rem] sm:text-[1.4rem] drop-shadow-[0_0_15px_rgba(74,222,128,1)] animate-pulse text-center">
                                    Click For Info
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content grid: left = AttendeeStrip, right = Countdown */}
            <div className="relative z-10 max-w-7xl mx-auto w-full flex-1 flex items-end pb-6 sm:pb-10">
                <div className="w-full flex flex-col lg:flex-row items-center lg:items-end justify-between gap-4 sm:gap-6">

                    {/* Left: Attendee strip */}
                    <motion.div
                        {...fadeUp}
                        transition={{ duration: 0.7, delay: 0.3 }}
                        className="pointer-events-auto w-full max-w-[340px] lg:max-w-none flex justify-center lg:justify-start pt-[50px] lg:pt-0"
                    >
                        <AttendeeStrip refreshKey={attendeeRefreshKey} />
                    </motion.div>

                    {/* Right: Countdown timer */}
                    <motion.div
                        {...fadeUp}
                        transition={{ duration: 0.7, delay: 0.5 }}
                        className="pointer-events-auto w-full max-w-[340px] lg:max-w-none flex justify-center lg:justify-end"
                    >
                        <CountdownTimer />
                    </motion.div>

                </div>
            </div>

            {/* Bottom marquee strip */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.7 }}
                className="relative z-10 mt-10 -mx-4 bg-[#FACC15] border-y border-black/10 py-3"
                data-testid="hero-marquee"
            >
                <Marquee gradient={false} speed={55} pauseOnHover>
                    {Array.from({ length: 6 }).map((_, i) => (
                        <span
                            key={i}
                            className="font-display font-black text-slate-900 uppercase tracking-[0.2em] text-sm sm:text-base mx-8 flex items-center gap-8"
                        >
                            Free Food
                            <span className="text-[#0D9488]">◆</span>
                            Free Drink
                            <span className="text-[#FF5E3A]">◆</span>
                            Free Accommodation
                            <span className="text-[#567f9b]">◆</span>
                        </span>
                    ))}
                </Marquee>
            </motion.div>

            {/* Modal overlay for info image */}
            <AnimatePresence>
                {showModal && (
                    <motion.div
                        key="modal-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[200] flex items-center justify-center p-2 sm:p-4 bg-black/60 backdrop-blur-sm"
                        onClick={handleModalClose}
                    >
                        <motion.div
                            key="modal-content"
                            initial={{ opacity: 0, scale: 0.9, y: 30 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 30 }}
                            transition={{ duration: 0.35, ease: "easeOut" }}
                            className="relative w-full max-w-4xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                onClick={handleModalClose}
                                className="absolute -top-3 -right-3 sm:-top-5 sm:-right-5 z-10 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-slate-800 flex items-center justify-center shadow-lg transition-colors"
                                aria-label="Close information"
                            >
                                <X className="w-5 h-5" />
                            </button>
                            {/* Zoom controls */}
                            <div className="absolute top-2 left-2 z-10 flex gap-1.5">
                                <button onClick={() => setZoom((z) => clampZoom(z + 0.5))} className="w-9 h-9 rounded-full bg-white/90 hover:bg-white text-slate-800 flex items-center justify-center shadow-lg text-xl font-bold transition-colors">+</button>
                                <button onClick={() => { const next = clampZoom(zoom - 0.5); setZoom(next); if (next === 1) setPan({ x: 0, y: 0 }); }} className="w-9 h-9 rounded-full bg-white/90 hover:bg-white text-slate-800 flex items-center justify-center shadow-lg text-xl font-bold transition-colors">−</button>
                                {zoom > 1 && <button onClick={resetZoom} className="h-9 px-3 rounded-full bg-white/90 hover:bg-white text-slate-800 flex items-center justify-center shadow-lg text-xs font-bold transition-colors">Reset</button>}
                            </div>
                            <div
                                className="w-full bg-black rounded-xl overflow-hidden shadow-2xl relative select-none"
                                style={{ cursor: zoom > 1 ? "grab" : "zoom-in", touchAction: "none" }}
                                onWheel={handleWheel}
                                onPointerDown={handlePointerDown}
                                onPointerMove={handlePointerMove}
                                onPointerUp={handlePointerUp}
                                onTouchStart={handleTouchStart}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                                onDoubleClick={() => { if (zoom > 1) { resetZoom(); } else { setZoom(2.5); } }}
                            >
                                <img
                                    src="https://res.cloudinary.com/dyssxsxce/image/upload/v1784103217/AISFEST_STAFF_INFO_jt0qxl.png"
                                    alt="Important Information"
                                    className="w-full h-auto object-contain max-h-[85vh] pointer-events-none"
                                    draggable="false"
                                    style={{ transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`, transformOrigin: "center center", transition: isDragging.current ? "none" : "transform 0.15s ease-out" }}
                                />
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
